import { createContext, useContext, useSyncExternalStore } from "react";
import type { ThemeTokens } from "./tokens.types";

const PREFERS_DARK_QUERY = "(prefers-color-scheme: dark)";
const PREFERS_REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeToPrefersDark(onStoreChange: () => void) {
  const media = window.matchMedia(PREFERS_DARK_QUERY);
  media.addEventListener("change", onStoreChange);
  return () => media.removeEventListener("change", onStoreChange);
}

export function isDarkScheme(
  root: { dataset: { colorScheme?: string } } | null,
  prefersDark: boolean,
): boolean {
  const forced = root?.dataset.colorScheme;
  if (forced === "dark") {
    return true;
  }
  if (forced === "light") {
    return false;
  }
  return prefersDark;
}

function getPrefersDarkSnapshot() {
  return isDarkScheme(
    typeof document === "undefined" ? null : document.documentElement,
    window.matchMedia(PREFERS_DARK_QUERY).matches,
  );
}

function subscribeToPrefersReducedMotion(onStoreChange: () => void) {
  const media = window.matchMedia(PREFERS_REDUCED_MOTION_QUERY);
  media.addEventListener("change", onStoreChange);
  return () => media.removeEventListener("change", onStoreChange);
}

function getPrefersReducedMotionSnapshot() {
  return window.matchMedia(PREFERS_REDUCED_MOTION_QUERY).matches;
}

// Shared intro spring for consent chrome and ceremony caption.
export const INTRO_SPRING = {
  type: "spring" as const,
  stiffness: 220,
  damping: 26,
  mass: 0.9,
};

export const MARK_LAYOUT_SPRING = {
  type: "spring" as const,
  stiffness: 220,
  damping: 30,
  mass: 1.0,
};

// Verified beat: marks dip (anticipation) → spring back up as the pulse releases.
export const VERIFIED_ANTICIPATION_SCALE = 0.88;
export const VERIFIED_ANTICIPATION_MS = 280;
export const VERIFIED_RELEASE_SPRING = {
  type: "spring" as const,
  stiffness: 380,
  damping: 22,
  mass: 0.85,
};
export const COMPLETE_PULSE_DELAY_S = VERIFIED_ANTICIPATION_MS / 1000;

// Ceremony mark stack: separated after layout lands, then rejoin (no per-mark pulse halos).
export const CEREMONY_SETTLE_TO_JOIN_MS = 800;
export const CEREMONY_JOIN_TO_VERIFIED_MS = 150;

// Card geometry. Tuned for a 316x400 ID pass on a ~440px wide content area.
export const CARD_WIDTH = 316;
export const CARD_HEIGHT = 400;
export const HIT_BUFFER = 14;
export const CARD_RADIUS = 32;

// Outer card shell. Structural classes only; bg + border come from tokens
// so light/dark + Figma fidelity is driven by a single source.
export const CARD_SHELL_CLASS =
  "relative flex h-full w-full flex-col items-center justify-between overflow-hidden rounded-[32px] p-1";

// Frosted inner panel. Structural classes only; bg + border come from tokens.
// No backdrop-filter here: it's silently broken by the card's preserve-3d
// tilt ancestor (Mozilla 1952612 / Chromium 323735424), and the 3D tilt is
// load-bearing for the hologram feel — so the body relies on its tinted bg
// alone to read as distinct from the card surface.
export const GLASS_BODY_CLASS = "relative z-10 flex w-full flex-col overflow-hidden rounded-[30px]";

// Card hover / glass material constants. Frozen from lab tuning.
export const TILT_Y = 30;
export const TILT_X = 19;
export const PULL_X = 55;
export const PULL_Y = 55;

// Hero parallax. Counter-translation amplitude (px) for the seal + inner
// rings relative to the cursor input range of -0.5..+0.5. Smaller than
// PULL so the effect reads as a subtle depth shift, not a swimming hero.
export const HERO_PARALLAX_X = 22;
export const HERO_PARALLAX_Y = 22;

// Card scene motion. Tilt spring matches lab motionRoles.transition tuning.
export const CARD_TILT_SPRING = {
  type: "spring" as const,
  stiffness: 380,
  damping: 38,
  mass: 0.9,
};

export const CARD_PERSPECTIVE = 1200;
export const CARD_SEND_X = 620;
export const CARD_SEND_ROTATE = 14;
export const CARD_SEND_EASE: [number, number, number, number] = [0.5, 0, 0.85, 0.3];

// Identity card content reveal (glass body rows, divider, footnotes).
export const CARD_CONTENT_EASE: [number, number, number, number] = [0.5, 0, 0.75, 0];
const CARD_ROW_STAGGER_BASE = 0.78;
const CARD_ROW_STAGGER_STEP = 0.08;
export const CARD_ROW_DURATION = 0.4;
export const CARD_FOOTER_DURATION = 0.45;
export const CARD_FOOTER_DELAY = 1.05;
export const CARD_SHADOW_BLUR = 44;

export function cardRowDelay(stagger: number) {
  return CARD_ROW_STAGGER_BASE + stagger * CARD_ROW_STAGGER_STEP;
}

export const LIGHT_THEME: ThemeTokens = {
  cardText: "rgba(10,10,10,0.95)",
  cardTextSoft: "rgba(10,10,10,0.65)",
  cardTextDim: "rgba(10,10,10,0.45)",
  cardLabel: "rgba(49,49,49,0.7)",
  cardBlend: "normal",
  cardDivider: "rgba(10,10,10,0.25)",
  cardTintRgb: "255,255,255",
  cardBorder: "1px solid rgba(10,10,10,0.08)",
  cardShadow:
    "0 252px 71px 0 rgba(0,0,0,0), 0 162px 65px 0 rgba(0,0,0,0.01), 0 91px 55px 0 rgba(0,0,0,0.05), 0 40px 40px 0 rgba(0,0,0,0.09), 0 10px 22px 0 rgba(0,0,0,0.1)",
  cardInsetHighlight: "inset 0 1px 0 rgba(255,255,255,0.35)",
  cardOuterBgStyle: "linear-gradient(to top, rgba(10,10,10,0.04), rgba(10,10,10,0.04))",
  cardOuterBorder: "1px solid rgba(10,10,10,0.08)",
  bodyTintBg: "rgba(255,255,255,0.4)",
  bodyBorder: "1px solid rgba(255,255,255,0.5)",
  bodyShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
  bodyBgStyle: "linear-gradient(to top, rgba(255,255,255,0.4), rgba(255,255,255,0.5))",
  vignetteColor: "#fefefe",
  partnerBg: "#333",
  partnerFg: "#fefefe",
  markColor: "#0a0a0a",
  heroSealTone: "dark",
  ringStroke: "rgba(10,10,10,0.18)",
  ringStrokeWidth: 1,
  ringOpacityMin: 0.15,
  ringOpacityMax: 0.95,
  chipBg: "rgba(10,10,10,0.06)",
  chipBorder: "rgba(10,10,10,0.1)",
  chipText: "rgba(10,10,10,0.92)",
  chipIcon: "rgba(10,10,10,0.75)",
  hairline: "rgba(10,10,10,0.18)",
  verifiedLabel: "rgba(10,10,10,0.5)",
  rimHighlightGradient:
    "radial-gradient(320px at {x} {y}, rgba(10,10,10,0.45) 0%, rgba(10,10,10,0.18) 28%, rgba(10,10,10,0.05) 58%, transparent 90%)",
  pulseRing: "rgba(125,0,255,0.6)",
  pulseGlow: "rgba(125,0,255,0.24)",
  completePulseBackground: [
    "radial-gradient(circle at 50% 50%, transparent 0%, transparent 28%, rgba(125,0,255,0.95) 32%, rgba(125,0,255,0.65) 40%, transparent 48%)",
    "radial-gradient(circle at 50% 50%, transparent 0%, transparent 46%, rgba(125,0,255,0.2) 50%, transparent 58%)",
  ].join(", "),
};

export const DARK_THEME: ThemeTokens = {
  cardText: "rgba(254,254,254,0.96)",
  cardTextSoft: "rgba(254,254,254,0.78)",
  cardTextDim: "rgba(254,254,254,0.5)",
  cardLabel: "rgba(254,254,254,0.55)",
  cardBlend: "normal",
  cardDivider: "rgba(255,255,255,0.25)",
  cardTintRgb: "14,12,24",
  cardBorder: "1px solid rgba(255,255,255,0.08)",
  cardShadow:
    "0 252px 71px 0 rgba(0,0,0,0), 0 162px 65px 0 rgba(0,0,0,0.01), 0 91px 55px 0 rgba(0,0,0,0.05), 0 40px 40px 0 rgba(0,0,0,0.09), 0 10px 22px 0 rgba(0,0,0,0.1)",
  cardInsetHighlight: "inset 0 1px 0 rgba(255,255,255,0.06)",
  cardOuterBgStyle: "linear-gradient(to top, rgba(245,245,245,0.04), rgba(255,255,255,0.04))",
  cardOuterBorder: "1px solid rgba(255,255,255,0.08)",
  bodyTintBg: "rgba(255,255,255,0.1)",
  bodyBorder: "1px solid rgba(255,255,255,0.08)",
  bodyShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
  bodyBgStyle: "linear-gradient(to top, rgba(234,234,234,0.1), rgba(218,218,218,0.1))",
  vignetteColor: "oklch(0.18 0.012 280)",
  partnerBg: "#fefefe",
  partnerFg: "#0a0a0a",
  markColor: "#fefefe",
  heroSealTone: "light",
  ringStroke: "rgba(255,255,255,0.18)",
  ringStrokeWidth: 1,
  ringOpacityMin: 0.15,
  ringOpacityMax: 0.95,
  chipBg: "rgba(255,255,255,0.12)",
  chipBorder: "rgba(255,255,255,0.12)",
  chipText: "rgba(255,255,255,0.96)",
  chipIcon: "rgba(255,255,255,0.85)",
  hairline: "rgba(255,255,255,0.25)",
  verifiedLabel: "rgba(254,254,254,0.5)",
  rimHighlightGradient:
    "radial-gradient(320px at {x} {y}, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.32) 24%, rgba(255,255,255,0.08) 55%, transparent 90%)",
  pulseRing: "oklch(0.78 0.22 308 / 0.65)",
  pulseGlow: "oklch(0.72 0.26 305 / 0.16)",
  completePulseBackground: [
    "radial-gradient(circle at 50% 50%, transparent 0%, transparent 28%, oklch(0.72 0.26 305 / 0.95) 32%, oklch(0.78 0.22 308 / 0.65) 40%, transparent 48%)",
    "radial-gradient(circle at 50% 50%, transparent 0%, transparent 46%, oklch(0.72 0.26 305 / 0.2) 50%, transparent 58%)",
  ].join(", "),
};

/** Card chrome + typography tokens synced with prefers-color-scheme. */
export function useCardThemeTokens(): ThemeTokens {
  const prefersDark = useSyncExternalStore(
    subscribeToPrefersDark,
    getPrefersDarkSnapshot,
    () => false,
  );
  return prefersDark ? DARK_THEME : LIGHT_THEME;
}

/** True when the user has requested reduced motion. SSR-safe (defaults false). */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(
    subscribeToPrefersReducedMotion,
    getPrefersReducedMotionSnapshot,
    () => false,
  );
}

// Ambient ring breathing. Off under prefers-reduced-motion.
export const RING_BREATH_CYCLE_S = 5;
export const RING_BREATH_STAGGER_S = 0.45;
export const RING_BREATH_SCALE_MAX = 1.06;

const ThemeContext = createContext<ThemeTokens>(LIGHT_THEME);
export const ThemeProvider = ThemeContext.Provider;
export const useThemeTokens = () => useContext(ThemeContext);
